import { lazy, ReactNode, Suspense, useEffect, useState } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation, useParams } from "react-router-dom";
import { auth } from "../services/firebase/config";
import { User } from "firebase/auth";
import { Anchor, AppShell, Loader, Overlay, Stack, Text } from "@mantine/core";
import BaseTemplate from "../BaseTemplate";
import AppHeader from "../components/AppHeader";
import { useTranslation } from "react-i18next";
import { ModelMemorial } from "../models/ModelMemorial";
import { isSuperAdmin } from "../services/firebase/endpoints/reports";

// Route-level code splitting - each page (and everything it alone pulls in,
// e.g. the tiptap editor or admin-only code) loads only when its route is
// actually visited, instead of every visitor downloading all of it upfront.
const Home = lazy(() => import("./Home"));
const Memorial = lazy(() => import("./memorial/Memorial"));
const Terms = lazy(() => import("./Terms"));
const Privacy = lazy(() => import("./Privacy"));
const Login = lazy(() => import("./authentication/Login"));
const AccountMemorials = lazy(() => import("./account/Memorials").then((m) => ({ default: m.AccountMemorials })));
const MyStory = lazy(() => import("./MyStory").then((m) => ({ default: m.MyStory })));
const HowItWorks = lazy(() => import("./HowItWorks"));
const Features = lazy(() => import("./Features"));
const AccountMemorialDashboard = lazy(() =>
  import("./account/memorials/Dashboard").then((m) => ({ default: m.AccountMemorialDashboard }))
);
const AdminMemorialConsole = lazy(() => import("./admin/AdminMemorialConsole"));
const AdminMemorialReview = lazy(() => import("./admin/AdminMemorialReview"));
const AdminMemorialMonitorView = lazy(() => import("./admin/AdminMemorialMonitorView"));
const InviteAccept = lazy(() => import("./InviteAccept"));
const AccountNotifications = lazy(() =>
  import("./account/Notifications").then((m) => ({ default: m.AccountNotifications }))
);

const RouteFallback = () => (
  <BaseTemplate>
    <Loader size="xl" color="brand-gold" />
  </BaseTemplate>
);

// A legacy route's :id forwarded onto its replacement - see the old
// /admin/monitor/* routes below, kept only so bookmarks/old links still land
// somewhere sensible after Submissions and Monitor merged into one page.
function RedirectPreservingId({ to }: { to: (id: string) => string }) {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={id ? to(id) : "/admin/monitor"} replace />;
}

// The path a signed-out visitor was actually trying to reach (e.g. an
// /invite/:id link) - stashed here so /login can send them back to it after
// signing in, instead of always dropping them on /account/memorials.
// sessionStorage rather than router state: signInWithRedirect (see Login.tsx)
// does a full page navigation away and back, which would lose in-memory
// router state but survives in sessionStorage.
const POST_LOGIN_REDIRECT_KEY = "post_login_redirect";

// Both the stash (here) and the read (PostLoginRedirect below) only run
// inside components React actually mounts for the matched route - never as
// a plain function call during <Routes> children construction, which runs
// on every render regardless of which route currently matches.
function RequireAuth({
  isAuthLoading,
  currentUser,
  children,
}: {
  isAuthLoading: boolean;
  currentUser: User | undefined;
  children: ReactNode;
}) {
  const location = useLocation();
  if (isAuthLoading) return <RouteFallback />;
  if (currentUser) return <>{children}</>;
  try {
    sessionStorage.setItem(POST_LOGIN_REDIRECT_KEY, location.pathname + location.search);
  } catch (e) {
    // sessionStorage unavailable (private browsing, etc.) - just fall back
    // to the default post-login destination instead of the intended one.
  }
  return <Navigate to="/login" replace />;
}

function PostLoginRedirect() {
  const [target] = useState<string>(() => {
    try {
      const stashed = sessionStorage.getItem(POST_LOGIN_REDIRECT_KEY);
      if (stashed) {
        sessionStorage.removeItem(POST_LOGIN_REDIRECT_KEY);
        return stashed;
      }
    } catch (e) {
      // ignore - fall through to the default below
    }
    return "/account/memorials";
  });
  return <Navigate to={target} replace />;
}

export default function Router() {
  const { t } = useTranslation();

  const [currentUser, setCurrentUser] = useState<User | undefined>(undefined);
  const [isAuthLoading, setIsAuthLoading] = useState<boolean>(true);
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [isAdminLoading, setIsAdminLoading] = useState<boolean>(true);
  const [memorialData, setMemorialData] = useState<ModelMemorial | undefined>(undefined);

  useEffect(() => {
    return auth.onAuthStateChanged((user) => {
      // Anonymous sessions (silently created just to give a rate-limit uid
      // for tribute submissions - see endpoints/tributes.tsx) must never be
      // treated as a real signed-in account anywhere else in the app - the
      // header, route gating, and everything downstream of currentUser
      // assumes a real account, so anonymous users are filtered out here,
      // right at the source, rather than in every consumer.
      const realUser = user && !user.isAnonymous ? user : null;
      if (realUser) {
        localStorage.setItem("user_id", realUser.uid);
        // Waits for a fresh token before flipping isAuthLoading off, so a
        // stale/about-to-expire one isn't what every other page's first
        // Firestore read races against right after sign-in. But this is a
        // network call itself - on a flaky mobile connection it can fail
        // outright, and with no .catch() here that left isAuthLoading stuck
        // true forever: every gated route behind a permanent spinner until
        // a manual refresh gave it another chance. Sign-in already
        // succeeded (onAuthStateChanged fired with a real user) regardless
        // of whether this particular refresh call worked, so proceed either
        // way - any resulting stale-token race on a specific read is what
        // that read's own error handling is for.
        realUser
          .getIdToken()
          .catch((e) => console.error(e.message))
          .finally(() => {
            setCurrentUser(realUser);
            setIsAuthLoading(false);
          });
        isSuperAdmin(realUser.uid).then((admin) => {
          setIsAdmin(admin);
          setIsAdminLoading(false);
        });
      } else {
        localStorage.removeItem("user_id");
        setCurrentUser(undefined);
        setIsAuthLoading(false);
        setIsAdmin(false);
        setIsAdminLoading(false);
      }
    });
  }, []);

  // Same idea, but for the super-admin-only reports console - also waits on
  // the admins/{uid} Firestore lookup so a genuine admin refreshing the page
  // doesn't get bounced before that check resolves.
  const adminGatedElement = (element: ReactNode) =>
    isAuthLoading || isAdminLoading ? (
      <RouteFallback />
    ) : currentUser && isAdmin ? (
      element
    ) : (
      <Navigate to="/" replace />
    );

  return (
    <BrowserRouter>
      <AppShell
        navbar={{
          width: 0,
          breakpoint: "sm",
          // collapsed: { mobile: !opened },
        }}
        padding="md"
      >
        <AppShell.Header style={{ background: "transparent", borderBottom: "transparent" }}>
          <AppHeader currentUser={currentUser} isAdmin={isAdmin} />
          <Overlay zIndex={-1} backgroundOpacity={0} blur={8} />
        </AppShell.Header>

        <AppShell.Main>
          <Suspense fallback={<RouteFallback />}>
            <Routes>
              <Route path="/" element={<Home setMemorialData={setMemorialData} />} />
              <Route path="/my-story" element={<MyStory />} />
              <Route path="/how-it-works" element={<HowItWorks currentUser={currentUser} />} />
              <Route path="/features" element={<Features currentUser={currentUser} />} />
              <Route path="/login" element={currentUser ? <PostLoginRedirect /> : <Login />} />

              <Route path="/memorial/:id" element={<Memorial data={memorialData} />} />
              <Route path="/memorial" element={<Memorial />} />

              <Route
                path="/account/memorials"
                element={
                  <RequireAuth isAuthLoading={isAuthLoading} currentUser={currentUser}>
                    {currentUser && <AccountMemorials currentUser={currentUser} setMemorial={setMemorialData} />}
                  </RequireAuth>
                }
              />
              {/* Old split-view URL - the editor is a full page now (see
                  account/Memorials.tsx and AccountMemorialDashboard), so
                  any bookmarked or shared link here just forwards straight
                  to it, same as the legacy admin redirects below. */}
              <Route
                path="/account/memorials/:id"
                element={<RedirectPreservingId to={(id) => `/account/memorial/${id}/edit`} />}
              />
              {/* This page's own former URL, /dashboard - renamed to /edit. */}
              <Route
                path="/account/memorial/:id/dashboard"
                element={<RedirectPreservingId to={(id) => `/account/memorial/${id}/edit`} />}
              />
              <Route
                path="/account/memorial/:id/edit"
                element={
                  <RequireAuth isAuthLoading={isAuthLoading} currentUser={currentUser}>
                    {currentUser && <AccountMemorialDashboard currentUser={currentUser} memorial={memorialData} />}
                  </RequireAuth>
                }
              />
              <Route
                path="/invite/:id"
                element={
                  <RequireAuth isAuthLoading={isAuthLoading} currentUser={currentUser}>
                    {currentUser && <InviteAccept currentUser={currentUser} />}
                  </RequireAuth>
                }
              />
              <Route
                path="/account/notifications"
                element={
                  <RequireAuth isAuthLoading={isAuthLoading} currentUser={currentUser}>
                    {currentUser && <AccountNotifications currentUser={currentUser} />}
                  </RequireAuth>
                }
              />

              <Route
                path="/admin/monitor"
                element={adminGatedElement(currentUser && <AdminMemorialConsole currentUser={currentUser} />)}
              />
              <Route
                path="/admin/monitor/:id"
                element={adminGatedElement(currentUser && <AdminMemorialConsole currentUser={currentUser} />)}
              />
              <Route
                path="/admin/memorial/:id/review"
                element={adminGatedElement(currentUser && <AdminMemorialReview currentUser={currentUser} />)}
              />
              <Route
                path="/admin/memorial/:id/monitor"
                element={adminGatedElement(currentUser && <AdminMemorialMonitorView currentUser={currentUser} />)}
              />

              {/* Legacy links: /admin/monitor/:id/view predates the Submissions/
                  Monitor merge; /admin/memorials* was this merged page's own
                  URL for one release before the page was renamed back to
                  Monitor; /admin/reports predates Reports folding into
                  Monitor as its own "Reported" section, with every report
                  now reachable from the relevant memorial's own panel
                  there instead of a separate flat list. All three keep
                  forwarding to wherever things live now, preserving the id
                  where the old route had one. */}
              <Route path="/admin/monitor/:id/view" element={<RedirectPreservingId to={(id) => `/admin/memorial/${id}/monitor`} />} />
              <Route path="/admin/memorials" element={<Navigate to="/admin/monitor" replace />} />
              <Route path="/admin/memorials/:id" element={<RedirectPreservingId to={(id) => `/admin/monitor/${id}`} />} />
              <Route path="/admin/reports" element={<Navigate to="/admin/monitor" replace />} />

              <Route path="/privacy" element={<Privacy />} />
              <Route path="/terms" element={<Terms />} />
              {/* Old URL - keep working for anyone with it bookmarked or linked externally. */}
              <Route path="/terms-of-service" element={<Navigate to="/terms" replace />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
        </AppShell.Main>

        <AppShell.Footer style={{ background: "transparent", borderTop: "transparent" }}>
          <Stack gap={4} align="center" px="lg" py="md">
            <Text fz="sm" ta="center">
              © 2026 {t("general.name")} • <Anchor inherit>{t("general.support")}</Anchor> •{" "}
              <Anchor inherit onClick={() => (window.location.href = "/terms")}>
                {t("general.terms")}
              </Anchor>{" "}
              •{" "}
              <Anchor inherit onClick={() => (window.location.href = "/privacy")}>
                {t("general.privacyShort")}
              </Anchor>
            </Text>
            <Text fz={10} c="dimmed" ta="center" visibleFrom="sm">
              This site is protected by reCAPTCHA and the Google{" "}
              <a href="https://policies.google.com/privacy">Privacy Policy</a> and{" "}
              <a href="https://policies.google.com/terms">Terms of Service</a> apply.
            </Text>
          </Stack>
          <Overlay zIndex={-1} backgroundOpacity={0} blur={8} />
        </AppShell.Footer>
      </AppShell>
    </BrowserRouter>
  );
}
